import { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

/**
 * ChatInput — isolated textarea + send button.
 *
 * Why isolated? The parent Chat page renders ~800 lines of JSX. Keeping the
 * input state local to this child means each keystroke only re-renders this
 * tiny component, NOT the entire Chat tree — eliminating typing lag.
 *
 * Behavior (per Feb 2026 user spec):
 *  • Enter / Return → ALWAYS inserts a newline (no send-on-enter, on any device).
 *  • The gold send arrow is the only way to send a message.
 */
export default function ChatInput({ onSend, disabled = false, placeholder = 'Talk to Dea...' }) {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);

  const handleSubmit = useCallback(() => {
    const text = input.trim();
    if (!text || disabled) return;
    setInput('');
    onSend(text);
    // Keep mobile keyboard open after sending
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [input, disabled, onSend]);

  return (
    <div className="glass-card rounded-2xl p-3 md:p-4 flex items-end gap-3">
      <Textarea
        ref={textareaRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 bg-transparent border-0 text-[#726158] placeholder:text-[#B1897C] resize-none min-h-[48px] md:min-h-[56px] max-h-40 focus:ring-0 text-base md:text-lg disabled:cursor-not-allowed leading-relaxed py-2"
        rows={2}
        data-testid="chat-input"
      />
      <Button
        type="button"
        onClick={(e) => { e.preventDefault(); handleSubmit(); }}
        disabled={!input.trim() || disabled}
        className="btn-gold h-11 w-11 md:h-12 md:w-12 p-0 rounded-xl flex-shrink-0"
        data-testid="chat-send-btn"
      >
        <Send className="h-5 w-5 md:h-6 md:w-6" />
      </Button>
    </div>
  );
}
